import { clipBaseIdOf } from './background-boxes';
import { BoxType, isBoxLocked, type CustomTemplate, type CustomTemplateBox } from './custom-template';

// A clipper locks with its base — base and clippers are one clip unit: whatever lock set the walk produced,
// the clippers of its members join.
function withLockedClippers(model: CustomTemplate, ids: Set<string>): Set<string> {
  const walk = (boxes: readonly CustomTemplateBox[]): void => {
    for (const box of boxes) {
      const base = clipBaseIdOf(box);
      if (base && ids.has(base)) ids.add(box.id);
      if (box.type === BoxType.GROUP && 'boxes' in box) walk(box.boxes);
    }
  };

  walk(model.boxes ?? []);
  return ids;
}

export function isBoxLockedInModel(model: CustomTemplate, boxId: string): boolean {
  const walk = (boxes: readonly CustomTemplateBox[], ancestorLocked: boolean): boolean | null => {
    for (const box of boxes) {
      const locked = ancestorLocked || isBoxLocked(box);
      if (box.id === boxId) return locked;
      if (box.type === BoxType.GROUP && 'boxes' in box) {
        const found = walk(box.boxes, locked);
        if (found !== null) return found;
      }
    }

    return null;
  };

  return walk(model.boxes ?? [], false) ?? false;
}

export function ancestorLockedBoxIds(model: CustomTemplate): ReadonlySet<string> {
  const ids = new Set<string>();
  const walk = (boxes: readonly CustomTemplateBox[], ancestorLocked: boolean): void => {
    for (const box of boxes) {
      if (ancestorLocked) ids.add(box.id);
      if (box.type === BoxType.GROUP && 'boxes' in box) walk(box.boxes, ancestorLocked || isBoxLocked(box));
    }
  };

  walk(model.boxes ?? [], false);
  // A clipper cannot release its base's lock on its own: that lock counts as an ancestor's.
  const locked = lockedBoxIds(model);
  const withClippers = (boxes: readonly CustomTemplateBox[]): void => {
    for (const box of boxes) {
      const base = clipBaseIdOf(box);
      if (base && locked.has(base)) ids.add(box.id);
      if (box.type === BoxType.GROUP && 'boxes' in box) withClippers(box.boxes);
    }
  };

  withClippers(model.boxes ?? []);
  return ids;
}

export function lockedBoxIds(model: CustomTemplate): ReadonlySet<string> {
  const ids = new Set<string>();
  const walk = (boxes: readonly CustomTemplateBox[], ancestorLocked: boolean): void => {
    for (const box of boxes) {
      const locked = ancestorLocked || isBoxLocked(box);
      if (locked) ids.add(box.id);
      if (box.type === BoxType.GROUP && 'boxes' in box) walk(box.boxes, locked);
    }
  };

  walk(model.boxes ?? [], false);
  return withLockedClippers(model, ids);
}
